// src/onboarding/sheets/PickInterestsSheet.tsx
import React, { useMemo, useState } from 'react';
import { X, Tag, AlertCircle } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import InterestSelector from '../../components/InterestSelector';

interface Props {
  onClose: (ok: boolean) => void;
}

const PickInterestsSheet: React.FC<Props> = ({ onClose }) => {
  const { currentUser, updateUser } = useAuth();

  // Start from the user's saved interests if present
  const initial = useMemo<string[]>(
    () =>
      Array.isArray(currentUser?.interests)
        ? (currentUser!.interests as string[])
        : [],
    [currentUser?.interests]
  );

  const [selected, setSelected] = useState<string[]>(initial);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>('');

  const save = async () => {
    if (saving) return;
    setSaving(true);
    setError('');

    try {
      // Small hygiene: trim + dedupe + drop empties
      const cleaned = Array.from(
        new Set(
          (selected || [])
            .map((s) => (typeof s === 'string' ? s.trim() : ''))
            .filter(Boolean)
        )
      );

      await updateUser({ interests: cleaned }); // keep your column name
      onClose(true); // ✅ resolve onboarding step
    } catch (e) {
      console.error('[interests] save failed:', e);
      setError('Could not save your interests. Please try again.');
      onClose(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-4">
      <div className="flex justify-between items-center mb-4">
        <div>
          <h2 className="text-xl font-semibold text-contrast flex items-center">
            <Tag size={20} className="mr-2 text-secondary" />
            Pick Interests
          </h2>
          <p className="text-sm text-contrast-600 mt-1">
            Complete your profile to pass the Vybe Check 💫
          </p>
        </div>
        <button
          onClick={() => onClose(false)}
          className="text-contrast-500 hover:text-contrast p-1 rounded-full"
          aria-label="Close"
        >
          <X size={20} />
        </button>
      </div>

      <p className="text-contrast-400 mb-4">
        Choose a few to personalize your feed and match with like-minded Tribe members.
      </p>

      {error && (
        <div className="mb-4 bg-secondary/10 p-3 rounded-md flex items-start">
          <AlertCircle size={18} className="mr-2 mt-0.5 text-secondary flex-shrink-0" />
          <p className="text-secondary text-sm">{error}</p>
        </div>
      )}

      {/* Uses your InterestSelector exactly as shipped */}
      <InterestSelector
        selectedInterests={selected}
        onChange={setSelected}
        /* no availableInterests prop → uses your existing single source */
        allowCustom={false}
      />

      <div className="mt-2 text-xs text-contrast-500">
        Selected: {selected.length}
      </div>

      <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-2">
        <button
          onClick={save}
          disabled={saving}
          className="w-full bg-gradient-to-r from-red-500 to-pink-600 hover:from-red-600 hover:to-pink-700 text-white py-3 rounded-full transition-colors disabled:opacity-50 font-semibold shadow-lg"
        >
          {saving ? 'Saving…' : 'Save Interests'}
        </button>

        <button
          onClick={() => onClose(false)}
          className="w-full bg-primary-100 text-contrast py-3 rounded-md hover:bg-primary-200 transition-colors"
        >
          Skip for Now
        </button>
      </div>
    </div>
  );
};

export default PickInterestsSheet;
